"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import rolesService from "@/services/rolesService";

interface ISMSRole {
  id: string;
  roleName: string;
}

interface ISMSRoleComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  singleSelect?: boolean;
  onRoleSelect?: (roleId: string) => void;
  onRoleDeselect?: (roleId: string) => void;
  roles?: ISMSRole[];
  fetchRoles?: () => Promise<ISMSRole[]>;
}

export function ISMSRoleCombobox({
  selected,
  setSelected,
  placeholder = "Select a role...",
  className,
  fullWidth = false,
  singleSelect = false,
  onRoleSelect,
  onRoleDeselect,
  roles: propRoles,
  fetchRoles: propFetchRoles,
}: ISMSRoleComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [roles, setRoles] = React.useState<ISMSRole[]>(propRoles || []);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [loading, setLoading] = React.useState(!propRoles?.length);
  const [error, setError] = React.useState<Error | null>(null);

  // Use propRoles if provided, otherwise use internal state
  const displayRoles = propRoles || roles;

  React.useEffect(() => {
    const fetchRolesData = async () => {
      if (propFetchRoles) {
        try {
          setLoading(true);
          const fetchedRoles = await propFetchRoles();
          setRoles(fetchedRoles);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to fetch roles'));
          console.error('Error fetching roles:', err);
        } finally {
          setLoading(false);
        }
      } else if (!propRoles?.length) {
        // Default fetch if no roles provided
        try {
          setLoading(true);
          const data = await rolesService.getISMSRoles();
          const roleList = data.ismsRoles.map(role => ({
            id: role.id,
            roleName: role.roleName
          }));
          setRoles(roleList);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to fetch roles'));
          console.error('Error fetching roles:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchRolesData();
  }, [propFetchRoles, propRoles]);

  const handleSelect = (roleId: string) => {
    let newSelected: string[];
    
    if (singleSelect) {
      newSelected = selected[0] === roleId ? [] : [roleId];
    } else {
      newSelected = selected.includes(roleId)
        ? selected.filter((id) => id !== roleId)
        : [...selected, roleId];
    }
    
    setSelected(newSelected);
    
    if (singleSelect && newSelected.length === 0) {
      onRoleDeselect?.(roleId);
    } else if (singleSelect) {
      onRoleSelect?.(newSelected[0]);
    } else if (selected.includes(roleId)) {
      onRoleDeselect?.(roleId);
    } else {
      onRoleSelect?.(roleId);
    }
    
    if (singleSelect) {
      setOpen(false);
    }
  };

  const handleRemove = (roleId: string) => {
    const newSelected = selected.filter((id) => id !== roleId);
    setSelected(newSelected);
    onRoleDeselect?.(roleId);
  };

  const filteredRoles = displayRoles.filter((role) =>
    role.roleName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (error) {
    return <div className="text-destructive">Error loading roles</div>;
  }

  return (
    <div className={cn("space-y-2", className, { 'w-full': fullWidth })}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-controls="role-combobox-content"
            className={cn(
              "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              {
                'text-muted-foreground': selected.length === 0,
              },
              className
            )}
          >
            <div 
              className="flex flex-wrap gap-1 max-h-20 overflow-y-auto"
              onClick={() => setOpen(true)}
            >
              {selected.length > 0 ? (
                selected.map((roleId) => {
                  const role = displayRoles.find((r) => r.id === roleId);
                  if (!role) return null;
                  
                  return (
                    <HoverCard key={roleId}>
                      <HoverCardTrigger asChild>
                        <div className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm cursor-help">
                          <div className="flex items-center gap-2">
                            <span className="max-w-[200px] truncate text-sm">{role.roleName}</span>
                          </div>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label={`Remove ${role.roleName}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(roleId);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemove(roleId);
                              }
                            }}
                            className="opacity-50 hover:opacity-100 flex items-center justify-center h-4 w-4 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                          >
                            <X className="h-3 w-3" />
                          </div>
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80">
                        <div className="space-y-2">
                          <h4 className="font-medium leading-none">{role.roleName}</h4>
                          <p className="text-sm text-muted-foreground">
                            ID: {role.id}
                          </p>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );
                })
              ) : (
                <span className="text-sm">{placeholder}</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search roles..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandEmpty>No roles found.</CommandEmpty>
            <CommandGroup>
              <CommandList>
                {loading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Loading roles...
                  </div>
                ) : filteredRoles.length > 0 ? (
                  filteredRoles.map((role) => (
                    <CommandItem
                      key={role.id}
                      value={role.roleName}
                      onSelect={() => handleSelect(role.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center">
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selected.includes(role.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span>{role.roleName}</span>
                      </div>
                    </CommandItem>
                  ))
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No roles available
                  </div>
                )}
              </CommandList>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
